import React, { useState, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import redraft from 'redraft';
import { useCookies } from 'react-cookie';
import { ImageFromUrl, ButtonLink } from '@package/components';
import { isCmsUi } from '@plone/volto/helpers';
import {
  Button,
  Popup,
  Image,
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from 'semantic-ui-react';
import LogoModenaSmall from './logo-visitmodena-small.svg';

import { closeFab } from '@package/actions';

const messages = defineMessages({
  FabOverlay: {
    id: 'Fab',
    defaultMessage: 'FAB - Caratteristiche, vantaggi e benefici',
  },
  FabOverlayImage: {
    id: 'Immagine del FAB',
    defaultMessage: 'Immagine del FAB',
  },
  FabOverlayLabel: {
    id: 'fab_overlay-Scopri di più',
    defaultMessage: 'Scopri di più',
  },
  FabOverlayClose: {
    id: 'fab_overlay close',
    defaultMessage: 'Chiudi',
  },
});

const FabOverlay = ({ pathname, content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [cookies, setCookie] = useCookies();
  const fabSettings = JSON.parse(
    content?.['@components']?.['view-extra-data']?.['fab_configuration'] ??
      '[]',
  );
  const fabState = useSelector((state) => state.fab);
  const isCmsUI = isCmsUi(pathname);

  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  // Seleziona la configurazione attiva per il pathname corrente
  function getConfByPath(items, pathname) {
    let rootPathConfig = null;
    const itemsByPath = Array.isArray(items)
      ? items.reduce((acc, val) => {
          if (val.rootPath === '/') {
            rootPathConfig = val;
            return acc;
          }
          return { ...acc, [val.rootPath]: val };
        }, {})
      : [];

    const matchingPaths = Object.keys(itemsByPath)
      .filter((path) => pathname.startsWith(path))
      .sort((a, b) => b.length - a.length); // path più specifico prima

    if (matchingPaths.length > 0) return itemsByPath[matchingPaths[0]];
    else if (rootPathConfig) return rootPathConfig;
    else return null;
  }

  const activeFab =
    fabSettings.length > 0 && getConfByPath(fabSettings, pathname);

  // Controlla se il FAB per questo path è chiuso (cookie)
  const isFabClosed = (path) => {
    return cookies[`visitmodena-fab-closed-${path}`];
  };

  useEffect(() => {
    if (activeFab) {
      setOpen(!isFabClosed(activeFab.rootPath));
    } else {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, cookies, fabState]);

  const doCloseFab = () => {
    if (!activeFab) return;
    setOpen(false);
    // Imposta il cookie specifico per questo path
    setCookie(`visitmodena-fab-closed-${activeFab.rootPath}`, true, {
      path: activeFab.rootPath,
      maxAge: 1 * 24 * 60 * 60, // 1 giorno
    });
    dispatch(closeFab(activeFab.rootPath));
  };

  if (!activeFab?.fab) return null;
  if (isCmsUI) return null;

  return (
    <Popup
      className="fab-overlay-popup"
      trigger={
        <div className="fab-overlay-button" ref={triggerRef}>
          <Button title={intl.formatMessage(messages.FabOverlay)}>
            <Image
              src={LogoModenaSmall}
              alt={intl.formatMessage(messages.FabOverlay)}
              title={intl.formatMessage(messages.FabOverlay)}
              loading="lazy"
            />
          </Button>
        </div>
      }
      position="top right"
      on={['click']}
      hoverable
      onOpen={() => setOpen(true)}
      open={open}
      content={
        <>
          <button
            className="fab-overlay-close-button"
            onClick={doCloseFab}
            aria-label={
              intl.formatMessage(messages.FabOverlayClose) +
              ' ' +
              activeFab?.fab?.title
            }
          >
            &times;
          </button>
          <Card className="fab-overlay-card">
            {activeFab?.fab?.image && (
              <ImageFromUrl
                url={activeFab.fab.image[0]['@id'] ?? activeFab.fab.image}
                title={
                  activeFab.fab.image[0].title ??
                  intl.formatMessage(messages.FabOverlayImage)
                }
                size="mini"
              />
            )}
            <CardContent>
              <CardHeader>{activeFab?.fab?.title}</CardHeader>
              <CardDescription>
                {activeFab?.fab?.description?.text && (
                  <span>
                    {redraft(
                      activeFab.fab.description.text,
                      config.settings.richtextViewSettings.ToHTMLRenderers,
                      config.settings.richtextViewSettings.ToHTMLOptions,
                    )}
                  </span>
                )}
                {activeFab?.fab?.linkUrl && (
                  <div className="card-action">
                    <ButtonLink
                      color="red"
                      to={
                        activeFab.fab.linkUrl[0]['@id'] ?? activeFab.fab.linkUrl
                      }
                      title={
                        activeFab.fab?.labelUrl ??
                        intl.formatMessage(messages.FabOverlayLabel)
                      }
                      targetBlank={false}
                    />
                  </div>
                )}
              </CardDescription>
            </CardContent>
          </Card>
        </>
      }
    />
  );
};

export default FabOverlay;
